/*
 * Tipos do wire da projeção de fluxo de caixa (FE-2 CP-A — ADR 0026).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend em `master`
 * (`flow/api/projecao/*`). Regra de projeto INVIOLÁVEL: TODO valor monetário é
 * `string` aqui — nunca `number`. O wire entrega `NUMERIC(18,2)` serializado como
 * string decimal (ex.: `"1234567.89"`); convertê-lo para `number` em JS perderia
 * precisão (IEEE-754 não representa todo decimal exato e estoura acima de
 * `Number.MAX_SAFE_INTEGER`). O formatador em `../formato/decimal` opera sobre a
 * STRING, sem `Number`/`parseFloat`.
 */

/** Horizonte mínimo de dias projetados (inclusive). */
export const HORIZONTE_MIN = 1;
/** Horizonte máximo de dias projetados (inclusive). */
export const HORIZONTE_MAX = 365;
/** Horizonte padrão quando a UI não especifica (espelha o default do backend). */
export const HORIZONTE_DEFAULT = 120;

/**
 * Procedência do saldo inicial da projeção por empresa.
 *
 * `referencia_em`/`idade_minutos` são anuláveis: o backend devolve `null` quando
 * não há semente de saldo conhecida (a UI do CP-C exibe o estado correspondente).
 */
export interface ProcedenciaSaldoOut {
  fonte: string;
  referencia_em: string | null;
  idade_minutos: number | null;
}

/**
 * Um dia projetado da série. Os campos monetários (`entradas`/`saidas`/`saldo`)
 * são STRINGS decimais do wire — ver nota de precisão no topo do arquivo.
 *
 * As flags booleanas marcam características do dia que a UI sinaliza (vencida
 * puxada para hoje, receita atrasada não conciliada, conciliação pendente, dia
 * além do horizonte solicitado).
 */
export interface DiaProjetadoOut {
  data: string;
  entradas: string;
  saidas: string;
  saldo: string;
  vencida_puxada: boolean;
  receita_atrasada_nao_conciliada: boolean;
  conciliacao_pendente: boolean;
  alem_horizonte: boolean;
}

/** Projeção de uma EMPRESA (`GET /api/empresas/{empresa_id}/projecao`). */
export interface ProjecaoEmpresaOut {
  empresa_id: string;
  hoje: string;
  horizonte: number;
  saldo_inicial: string;
  procedencia: ProcedenciaSaldoOut;
  serie: DiaProjetadoOut[];
}

/**
 * Projeção do GRUPO (`GET /api/grupo/projecao`).
 *
 * SEM `procedencia` nem `saldo_inicial` — omissão INTENCIONAL do contrato: o ERP
 * não amarra lançamentos a contas, então a projeção do grupo é consolidada e não
 * tem uma única semente de saldo com procedência.
 */
export interface ProjecaoGrupoOut {
  hoje: string;
  horizonte: number;
  serie: DiaProjetadoOut[];
}

/**
 * Um item que SOMA na composição de um dia da projeção (drill-down — BE gap A).
 *
 * `valor` é STRING decimal do wire (regra inviolável — ver nota de precisão no
 * topo). `status`: `regular` (futuro/no prazo), `vencida` (`data_prevista <= hoje`,
 * não pago no ERP) ou `pendente_erp` (ERP diz Finalizado mas não conciliado).
 * `descricao` é anulável (nem todo título tem descrição). `dias_atraso` é o atraso
 * em dias quando `data_prevista <= hoje`, senão `null`.
 */
export interface ItemComposicao {
  id: string;
  tipo: "obrigacao" | "receita";
  descricao: string | null;
  valor: string;
  data_prevista: string;
  status: "regular" | "vencida" | "pendente_erp";
  dias_atraso: number | null;
}

/**
 * Receita pendente atrasada (`data_prevista <= hoje`) que NÃO soma na projeção do
 * dia. Existe só para transparência no drill-down de D+0; NUNCA entra em
 * `saldo_entradas`. `valor` é STRING decimal; `dias_atraso` é sempre presente
 * (a receita está, por definição, atrasada).
 */
export interface ReceitaAtrasada {
  id: string;
  descricao: string | null;
  valor: string;
  data_prevista: string;
  dias_atraso: number;
}

/**
 * Composição itemizada de um dia da projeção
 * (`GET /api/empresas/{empresa_id}/projecao/composicao?data=&horizonte=`).
 *
 * Reconciliação POR CONSTRUÇÃO no backend: `Σ itens[tipo=obrigacao].valor ==
 * saldo_saidas` e `Σ itens[tipo=receita].valor == saldo_entradas`. Os totais
 * (`saldo_saidas`/`saldo_entradas`) são STRINGS decimais — os mesmos que
 * `GET .../projecao` reporta no dia. `dia_semana` em pt-BR.
 */
export interface ComposicaoOut {
  data: string;
  dia_semana: string;
  saldo_saidas: string;
  saldo_entradas: string;
  itens: ItemComposicao[];
  receitas_atrasadas_nao_somadas: ReceitaAtrasada[];
}

/**
 * Empresa projetável pelo escopo do usuário (`GET /api/projecao/empresas`).
 *
 * `fantasia` é anulável (nem toda empresa cadastra nome fantasia). A lista pode
 * vir VAZIA (`[]`) quando o escopo do usuário não inclui empresa alguma — o tipo
 * já contempla; o tratamento de "escopo vazio" na UI é do CP-B.
 */
export interface EmpresaProjetavelOut {
  id: string;
  razao_social: string;
  fantasia: string | null;
  cnpj: string;
}
